import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Card, CardContent, Chip,
  IconButton, TextField, Tooltip, Stack, MenuItem,
  ToggleButton, ToggleButtonGroup, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, InputAdornment,
  Alert, CircularProgress, Divider, List, FormControlLabel, Switch
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Quiz as QuizIcon, GridView as GridViewIcon, FormatListBulleted as TableListIcon,
  ArrowBack as ArrowBackIcon, Link as LinkIcon, HelpOutline as QuestionsIcon,
  Assessment as ResultsIcon, ContentCopy as CopyIcon, CheckCircle as CheckIcon,
  Send as SendIcon, Refresh as RefreshIcon, Share as ShareIcon, AddCircleOutline as AddLevelIcon
} from '@mui/icons-material';
import { academyApi } from '../services/academyApi';
import { academyStore } from '../services/academyStore';
import { Quiz, QuizLevel } from '../types/academy';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import blobticService from '../../Commun/services/blobticService';
import { useNavigate } from 'react-router-dom';

const ENTITY_KEY = 'Blobtic_Academy,academy_quiz';
const PRIMARY_KEY = 'QIZ_IDF';

const fsx = {
  '& .MuiInputBase-root': { fontSize: '0.85rem' },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
};

const dtoToQuiz = (dto: any): Quiz => ({
  id: dto.QIZ_IDF,
  code: dto.QIZ_CODE || '',
  title: dto.QIZ_TITLE || '',
  description: dto.QIZ_DESCRIPTION || '',
  duration_minutes: Number(dto.QIZ_DURATION_MINUTES) || 5,
  expiry_days: Number(dto.QIZ_EXPIRY_DAYS) || 7,
  whatsapp: dto.QIZ_WHATSAPP || undefined,
  register_link: dto.QIZ_REGISTER_LINK || undefined,
  display: (dto.QIZ_DISPLAY === 'assistant' ? 'assistant' : 'list') as Quiz['display'],
  auto_next: Boolean(dto.QIZ_AUTO_NEXT),
  QIZ_AUTO_NEXT: Boolean(dto.QIZ_AUTO_NEXT),
  max_attempts_link: Number(dto.QIZ_MAX_ATTEMPTS_LINK) || 50,
  max_attempts_user: Number(dto.QIZ_MAX_ATTEMPTS_USER) || 3,
  QIZ_MAX_ATTEMPTS_LINK: Number(dto.QIZ_MAX_ATTEMPTS_LINK) || 50,
  QIZ_MAX_ATTEMPTS_USER: Number(dto.QIZ_MAX_ATTEMPTS_USER) || 3,
  status: (dto.QIZ_STATUS || 'Draft') as Quiz['status'],
  start_date: dto.QIZ_START_DATE ? new Date(dto.QIZ_START_DATE).toISOString().split('T')[0] : undefined,
  end_date: dto.QIZ_END_DATE ? new Date(dto.QIZ_END_DATE).toISOString().split('T')[0] : undefined,
  dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
  dos_code: dto.DOS_CODE || undefined,
  dos_lib: dto.DOS_LIB || undefined,
  user_lib: dto.USER_LIB || undefined,
  last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
});

const quizToDto = (q: Partial<Quiz>): Record<string, any> => ({
  QIZ_IDF: (q as any).QIZ_IDF ?? q.id,
  QIZ_CODE: q.code || '',
  QIZ_TITLE: q.title || '',
  QIZ_DESCRIPTION: q.description || '',
  QIZ_DURATION_MINUTES: q.duration_minutes ?? 5,
  QIZ_EXPIRY_DAYS: q.expiry_days ?? 7,
  QIZ_WHATSAPP: q.whatsapp || '',
  QIZ_REGISTER_LINK: q.register_link || '',
  QIZ_DISPLAY: q.display || 'list',
  QIZ_AUTO_NEXT: q.auto_next ?? q.QIZ_AUTO_NEXT ?? false,
  QIZ_MAX_ATTEMPTS_LINK: q.max_attempts_link ?? q.QIZ_MAX_ATTEMPTS_LINK ?? 50,
  QIZ_MAX_ATTEMPTS_USER: q.max_attempts_user ?? q.QIZ_MAX_ATTEMPTS_USER ?? 3,
  QIZ_STATUS: q.status || 'Draft',
  QIZ_START_DATE: q.start_date || null,
  QIZ_END_DATE: q.end_date || null,
  ...(q.dos_idf ? { DOS_IDF: q.dos_idf } : {}),
});

const emptyQuiz = (): Partial<Quiz> => ({
  code: '',
  title: '',
  description: '',
  duration_minutes: 5,
  expiry_days: 7,
  whatsapp: '',
  register_link: '',
  display: 'list',
  auto_next: true,
  QIZ_AUTO_NEXT: true,
  max_attempts_link: 50,
  max_attempts_user: 3,
  QIZ_MAX_ATTEMPTS_LINK: 50,
  QIZ_MAX_ATTEMPTS_USER: 3,
  status: 'Draft',
});

const emptyLevel = (quizId: number): Partial<QuizLevel> => ({
  quiz_id: quizId,
  code: '',
  title: '',
  min_score: 0,
  max_score: 0,
  message: '',
});

export const QuizzesPage: React.FC = () => {
  const { t } = useLanguage();
  
  const navigate = useNavigate();

  const [view, setView] = useState<'list' | 'form'>('list');
  const [viewMode, setViewMode] = useDefaultViewMode();
  const [quizzesList, setQuizzesList] = useState<Quiz[]>(() => academyStore.getQuizzes());
  const [listLoading, setListLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<Quiz>>(emptyQuiz());
  const [levels, setLevels] = useState<Partial<QuizLevel>[]>([]);
  const [levelsLoading, setLevelsLoading] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    content: string;
    color?: 'primary' | 'error';
    onConfirm: () => void;
  }>({ open: false, title: '', content: '', onConfirm: () => {} });

  // Share link dialog
  const [shareDialog, setShareDialog] = useState<{ open: boolean; quiz: Quiz | null }>({ open: false, quiz: null });
  const [linkLoading, setLinkLoading] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    setQuizzesList(academyStore.getQuizzes());
    return academyStore.subscribe(() => {
      setQuizzesList(academyStore.getQuizzes());
    });
  }, []);

  const fetchLevels = useCallback(async (quizId: number) => {
    setLevelsLoading(true);
    try {
      const list = await academyApi.getQuizLevels(quizId);
      setLevels(list || []);
    } catch (e: any) {
      blobticMessage.error(t('academy.quizzes.error_load_levels'), e.message);
    } finally {
      setLevelsLoading(false);
    }
  }, [blobticMessage.error, t]);

  const openNew = () => {
    setFormData(emptyQuiz());
    setLevels([]);
    setView('form');
  };

  const openEdit = (quiz: Quiz) => {
    setFormData({ ...quiz });
    setView('form');
    fetchLevels(quiz.id);
  };

  const backToList = () => {
    setView('list');
    setFormData(emptyQuiz());
    setLevels([]);
  };

  const setField = <K extends keyof Quiz>(key: K, value: Quiz[K]) =>
    setFormData(prev => ({ ...prev, [key]: value }));

  const handleValidate = (): string | null => {
    if (!formData.title?.trim()) return t('academy.quizzes.val_title');
    if (formData.duration_minutes == null || formData.duration_minutes <= 0) return t('academy.quizzes.val_duration');
    if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
      return t('academy.quizzes.val_date_range');
    }
    return null;
  };

  // GenericPage auto-updates IndexedDB → academyStore refreshes via subscription.
  const handleDataChange = () => backToList();

  const handleClear = () => backToList();


  const handleDeleteFromList = (quiz: Quiz) => {
    setConfirmDialog({
      open: true,
      title: t('common.confirm.title.delete') || 'Confirmer la suppression',
      content: t('academy.quizzes.confirm_delete', quiz.title),
      color: 'error',
      onConfirm: async () => {
        try {
          await academyStore.deleteQuiz(quiz.id);
          blobticMessage.success(t('common.messages.success'), t('common.messages.deleted_success'));
        } catch (e: any) {
          blobticMessage.error('Erreur suppression', e.message);
        }
      },
    });
  };

  // ─── Levels management ──────────────────────────────────────────────
  const saveLevel = async (level: Partial<QuizLevel>) => {
    if (!formData.id) return;
    const payload = { ...level, quiz_id: formData.id };
    const res = level.id
      ? await academyApi.updateQuizLevel(level.id, payload)
      : await academyApi.createQuizLevel(payload);
    if (res?.success) {
      blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));
      fetchLevels(formData.id);
    } else {
      blobticMessage.error(t('academy.quizzes.error_save_level'), res?.message || '');
    }
  };

  const deleteLevel = async (level: Partial<QuizLevel>) => {
    if (!level.id || !formData.id) return;
    try {
      await academyApi.deleteQuizLevel(level.id);
      blobticMessage.success(t('common.messages.success'), t('common.messages.deleted_success'));
      fetchLevels(formData.id);
    } catch (e: any) {
      blobticMessage.error(t('academy.quizzes.error_delete_level'), e.message);
    }
  };

  // ─── Share link ─────────────────────────────────────────────────────
  const openShare = (quiz: Quiz) => {
    setShareDialog({ open: true, quiz });
    setGeneratedUrl('');
    setLinkError(null);
    handleGenerateLink(quiz);
  };

  const handleGenerateLink = async (quiz: Quiz, forceNew = false) => {
    setLinkLoading(true);
    setLinkError(null);
    try {
      const res = await blobticService.executeZoomRequest<any>({
        ZoomKey: 'Blobtic_API',
        Action: {
          OKey: 'Blobtic_API,sys_api,generate_public_link',
          DOS_IDF: quiz.dos_idf,
          API_PAGE: 'academy-quiz',
          QIZ_IDF: quiz.id,
          FORCE_NEW: forceNew
        }
      });
      if (res && res.success && res.data) {
        const item = Array.isArray(res.data) ? res.data[0] : res.data;
        const token = item?.API_TOKEN || item?.token || '';
        setGeneratedUrl(`${window.location.origin}/academy-quiz?token=${token}`);
      } else {
        setLinkError(res?.message || t('academy.quizzes.share.error'));
      }
    } catch (err: any) {
      setLinkError(err?.message || t('academy.quizzes.share.error_generic'));
    } finally {
      setLinkLoading(false);
    }
  };

  const handleCopy = () => {
    if (!generatedUrl) return;
    navigator.clipboard.writeText(generatedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const statusColor = (status: string) =>
    status === 'Active' ? 'success' : status === 'Draft' ? 'default' : 'warning';

  // ─── LIST VIEW ──────────────────────────────────────────────────────
  if (view === 'list') {
    return (
      <GenericPage
        title={t('academy.quizzes.title')}
        icon={<QuizIcon />}
        footerEnabled={false}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onNew={openNew}
        newButtonLabel={t('academy.quizzes.add_new')}
      >
        {listLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">{t('common.loading') || 'Chargement...'}</Typography>
          </Box>
        ) : quizzesList.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 3, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
            <QuizIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
            <Typography variant="subtitle1" color="text.secondary">
              {t('academy.quizzes.empty')}
            </Typography>
          </Paper>
        ) : viewMode === 'grid' ? (
          <Grid container spacing={2.5}>
            {quizzesList.map(quiz => (
              <Grid item xs={12} sm={6} md={4} key={quiz.id}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    transition: 'transform 0.2s',
                    '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ flex: 1, pr: 1 }}>
                        {quiz.title}
                      </Typography>
                      <QuizIcon color="primary" />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                      <Chip label={quiz.code} size="small" color="info" variant="outlined" />
                      <Chip
                        label={t('academy.quizzes.duration_label', quiz.duration_minutes)}
                        size="small"
                        variant="outlined"
                      />
                      <Chip label={t(quiz.status === 'Active' ? 'academy.quizzes.status_active' : quiz.status === 'Draft' ? 'academy.quizzes.status_draft' : 'academy.quizzes.status_archived')} size="small" color={statusColor(quiz.status)} />
                    </Box>

                    {quiz.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {quiz.description.length > 120 ? `${quiz.description.slice(0, 120)}…` : quiz.description}
                      </Typography>
                    )}

                    {(quiz.start_date || quiz.end_date) && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        {quiz.start_date || '—'} → {quiz.end_date || '—'}
                      </Typography>
                    )}

                    <Divider sx={{ my: 1.5 }} />

                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                      <Tooltip title={t('academy.quizzes.actions.questions')}>
                        <IconButton size="small" color="info" onClick={() => navigate(`/Academy/quiz-questions?qiz_id=${quiz.id}`)}>
                          <QuestionsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('academy.quizzes.actions.results')}>
                        <IconButton size="small" color="warning" onClick={() => navigate(`/Academy/quiz-results?qiz_id=${quiz.id}`)}>
                          <ResultsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('academy.quizzes.actions.share')}>
                        <IconButton size="small" color="success" onClick={() => openShare(quiz)}>
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('academy.quizzes.actions.edit')}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(quiz)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('academy.quizzes.actions.delete')}>
                        <IconButton size="small" color="error" onClick={() => handleDeleteFromList(quiz)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <GenericTable
            columns={[
              { id: 'code', label: t('academy.quizzes.table.code'), minWidth: 110 },
              { id: 'title', label: t('academy.quizzes.table.title'), minWidth: 200 },
              { id: 'duration_minutes', label: t('academy.quizzes.table.duration'), minWidth: 100 },
              { id: 'status', label: t('academy.quizzes.table.status'), minWidth: 100 },
              {
                id: 'validity',
                label: t('academy.quizzes.table.validity'),
                minWidth: 180,
                format: (_val: any, row?: any) => `${row?.start_date || '—'} → ${row?.end_date || '—'}`
              },
            ]}
            data={quizzesList}
            onEdit={(row) => openEdit(quizzesList.find(c => c.id === row.id)!)}
            onDelete={(row) => handleDeleteFromList(quizzesList.find(c => c.id === row.id)!)}
            rowActions={(row) => (
              <>
                <Tooltip title={t('academy.quizzes.actions.questions')}>
                  <IconButton size="small" color="info" onClick={() => navigate(`/Academy/quiz-questions?qiz_id=${row.id}`)}>
                    <QuestionsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('academy.quizzes.actions.results')}>
                  <IconButton size="small" color="warning" onClick={() => navigate(`/Academy/quiz-results?qiz_id=${row.id}`)}>
                    <ResultsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('academy.quizzes.actions.share')}>
                  <IconButton size="small" color="success" onClick={() => openShare(quizzesList.find(c => c.id === row.id)!)}>
                    <LinkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            searchPlaceholder={t('academy.quizzes.search_placeholder')}
            maxHeight="calc(100vh - 380px)"
          />
        )}

        {/* Share link dialog */}
        <Dialog open={shareDialog.open} onClose={() => setShareDialog(prev => ({ ...prev, open: false }))} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
            <LinkIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              {t('academy.quizzes.share.title', shareDialog.quiz?.title || '')}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            {linkError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{linkError}</Alert>
            )}

            {linkLoading && !generatedUrl ? (
              <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={40} color="primary" />
                <Typography variant="body2" color="text.secondary">{t('academy.quizzes.share.generating')}</Typography>
              </Box>
            ) : generatedUrl ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  value={generatedUrl}
                  inputProps={{ readOnly: true }}
                  variant="outlined"
                  label={t('academy.quizzes.share.url_label')}
                  sx={{
                    mt: 1,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'action.hover',
                      borderRadius: 2,
                      '& .MuiOutlinedInput-input': { py: 1.25 },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={copied ? t('academy.quizzes.share.copied') : t('academy.quizzes.share.copy')}>
                          <IconButton color={copied ? 'success' : 'primary'} onClick={handleCopy}>
                            {copied ? <CheckIcon /> : <CopyIcon />}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    )
                  }}
                />
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" color="primary" fullWidth startIcon={copied ? <CheckIcon /> : <CopyIcon />} onClick={handleCopy} sx={{ textTransform: 'none', borderRadius: 2 }}>
                    {copied ? t('academy.quizzes.share.copied') : t('academy.quizzes.share.copy')}
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    component="a"
                    href={`https://api.whatsapp.com/send?text=${encodeURIComponent(t('academy.quizzes.share.whatsapp_text') + generatedUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    {t('academy.quizzes.share.whatsapp')}
                  </Button>
                </Stack>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    color="inherit"
                    disabled={linkLoading}
                    startIcon={linkLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon fontSize="small" />}
                    onClick={() => shareDialog.quiz && handleGenerateLink(shareDialog.quiz, true)}
                    sx={{ textTransform: 'none', fontSize: '0.8rem', opacity: 0.75 }}
                  >
                    {t('academy.quizzes.share.regenerate')}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
                <ShareIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1.5, opacity: 0.8 }} />
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  {t('academy.quizzes.share.empty_title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('academy.quizzes.share.empty_desc')}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={linkLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  onClick={() => shareDialog.quiz && handleGenerateLink(shareDialog.quiz)}
                  disabled={linkLoading}
                  sx={{ borderRadius: 2, textTransform: 'none', px: 4, fontWeight: 700 }}
                >
                  {linkLoading ? t('academy.quizzes.share.generating') : t('academy.quizzes.share.generate')}
                </Button>
              </Paper>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={() => setShareDialog(prev => ({ ...prev, open: false }))} color="inherit" sx={{ textTransform: 'none', fontWeight: 600 }}>
              {t('btn.close')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>{confirmDialog.content}</DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))} variant="outlined" color="inherit">
              {t('btn.cancel')}
            </Button>
            <Button
              onClick={() => {
                const action = confirmDialog.onConfirm;
                setConfirmDialog(prev => ({ ...prev, open: false }));
                action();
              }}
              autoFocus
              color={confirmDialog.color || 'primary'}
              variant="contained"
            >
              {t('common.actions.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </GenericPage>
    );
  }

  // ─── FORM VIEW ──────────────────────────────────────────────────────

  const isEditing = !!(formData as any).QIZ_IDF || !!formData.id;
  const formPayload = quizToDto(formData);

  return (
    <GenericPage
      title={
        isEditing
          ? t('academy.quizzes.form.title_edit', formData.title || '')
          : t('academy.quizzes.form.title_new')
      }
      icon={<QuizIcon />}
      footerEnabled={true}
      footerInfo={{
        lastUpdate: formData.last_updt || '-',
        userLib: formData.user_lib || '-',
      }}
      additionalActions={
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={backToList}>
          {t('academy.quizzes.back_to_list')}
        </Button>
      }
      crudConfig={{
        entityKey: ENTITY_KEY,
        actionSuffix: '',
        primaryKey: PRIMARY_KEY,
      }}
      data={formPayload}
      onDataChange={() => handleDataChange()}

      onValidate={handleValidate}
      onClear={handleClear}
    >
      <Box sx={{ maxWidth: 860, mx: 'auto', pb: 4 }}>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {t('academy.quizzes.section_info')}
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('academy.quizzes.form.code')}
                size="small"
                fullWidth
                sx={fsx}
                value={formData.code ?? ''}
                onChange={e => setField('code', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label={t('academy.quizzes.form.title')}
                size="small"
                fullWidth
                required
                sx={fsx}
                value={formData.title ?? ''}
                onChange={e => setField('title', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('academy.quizzes.form.duration')}
                type="number"
                size="small"
                fullWidth
                required
                sx={fsx}
                value={formData.duration_minutes ?? 5}
                onChange={e => setField('duration_minutes', Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('academy.quizzes.form.expiry_days')}
                type="number"
                size="small"
                fullWidth
                sx={fsx}
                value={formData.expiry_days ?? 7}
                onChange={e => setField('expiry_days', Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('academy.quizzes.form.status')}
                select
                size="small"
                fullWidth
                sx={fsx}
                value={formData.status ?? 'Draft'}
                onChange={e => setField('status', e.target.value as Quiz['status'])}
              >
                <MenuItem value="Draft">{t('academy.quizzes.status_draft')}</MenuItem>
                <MenuItem value="Active">{t('academy.quizzes.status_active')}</MenuItem>
                <MenuItem value="Archived">{t('academy.quizzes.status_archived')}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label={t('academy.quizzes.form.start_date')}
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={fsx}
                value={formData.start_date ?? ''}
                onChange={e => setField('start_date', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('academy.quizzes.form.end_date')}
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={fsx}
                value={formData.end_date ?? ''}
                onChange={e => setField('end_date', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('academy.quizzes.form.display')}
                select
                size="small"
                fullWidth
                sx={fsx}
                value={formData.display ?? 'list'}
                onChange={e => setField('display', e.target.value as Quiz['display'])}
              >
                <MenuItem value="list">{t('academy.quizzes.display_list')}</MenuItem>
                <MenuItem value="assistant">{t('academy.quizzes.display_assistant')}</MenuItem>
              </TextField>
            </Grid>            
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('academy.quizzes.form.whatsapp')}
                size="small"
                fullWidth
                sx={fsx}
                value={formData.whatsapp ?? ''}
                onChange={e => setField('whatsapp', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label={t('academy.quizzes.form.register_link')}
                size="small"
                fullWidth
                sx={fsx}
                value={formData.register_link ?? ''}
                onChange={e => setField('register_link', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('academy.quizzes.form.max_attempts_link')}
                type="number"
                size="small"
                fullWidth
                sx={fsx}
                inputProps={{ min: 1 }}
                value={formData.max_attempts_link ?? formData.QIZ_MAX_ATTEMPTS_LINK ?? 50}
                onChange={e => {
                  const val = parseInt(e.target.value) || 50;
                  setField('max_attempts_link', val);
                  setField('QIZ_MAX_ATTEMPTS_LINK', val);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('academy.quizzes.form.max_attempts_user')}
                type="number"
                size="small"
                fullWidth
                sx={fsx}
                inputProps={{ min: 1 }}
                value={formData.max_attempts_user ?? formData.QIZ_MAX_ATTEMPTS_USER ?? 3}
                onChange={e => {
                  const val = parseInt(e.target.value) || 3;
                  setField('max_attempts_user', val);
                  setField('QIZ_MAX_ATTEMPTS_USER', val);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formData.auto_next ?? formData.QIZ_AUTO_NEXT)}
                    onChange={(e) => {
                      setField('auto_next', e.target.checked);
                      setField('QIZ_AUTO_NEXT', e.target.checked);
                    }}
                    color="primary"
                  />
                }
                label={t('academy.quizzes.form.auto_next')}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label={t('academy.quizzes.form.description')}
                size="small"
                fullWidth
                multiline
                rows={3}
                sx={fsx}
                value={formData.description ?? ''}
                onChange={e => setField('description', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        {isEditing && (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                {t('academy.quizzes.section_levels')}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<AddLevelIcon />}
                onClick={() => setLevels(prev => [...prev, emptyLevel(formData.id!)])}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                {t('academy.quizzes.add_level')}
              </Button>
            </Box>

            {levelsLoading ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>{t('common.loading')}</Typography>
            ) : levels.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                {t('academy.quizzes.levels_empty')}
              </Typography>
            ) : (
              <List disablePadding>
                {levels.map(level => (
                  <Paper key={level.id || `new-${level.code || level.title}`} variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <TextField label={t('academy.quizzes.level.code')} size="small" fullWidth sx={fsx}
                          value={level.code ?? ''}
                          onChange={e => {
                            const val = { ...level, code: e.target.value };
                            setLevels(prev => prev.map(l => l === level ? val : l));
                          }} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField label={t('academy.quizzes.level.title')} size="small" fullWidth sx={fsx}
                          value={level.title ?? ''}
                          onChange={e => {
                            const val = { ...level, title: e.target.value };
                            setLevels(prev => prev.map(l => l === level ? val : l));
                          }} />
                      </Grid>
                      <Grid item xs={4} sm={2}>
                        <TextField label={t('academy.quizzes.level.min')} type="number" size="small" fullWidth sx={fsx}
                          value={level.min_score ?? 0}
                          onChange={e => {
                            const val = { ...level, min_score: Number(e.target.value) };
                            setLevels(prev => prev.map(l => l === level ? val : l));
                          }} />
                      </Grid>
                      <Grid item xs={4} sm={2}>
                        <TextField label={t('academy.quizzes.level.max')} type="number" size="small" fullWidth sx={fsx}
                          value={level.max_score ?? 0}
                          onChange={e => {
                            const val = { ...level, max_score: Number(e.target.value) };
                            setLevels(prev => prev.map(l => l === level ? val : l));
                          }} />
                      </Grid>
                      <Grid item xs={4} sm={2}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ height: '100%', alignItems: 'center' }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => saveLevel(level)}
                            sx={{ textTransform: 'none', borderRadius: 2, minWidth: 60 }}
                          >
                            {level.id ? t('academy.quizzes.level.update') : t('academy.quizzes.level.add')}
                          </Button>
                          <IconButton size="small" color="error" onClick={() => deleteLevel(level)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField label={t('academy.quizzes.level.message')} size="small" fullWidth multiline rows={1} sx={fsx}
                          value={level.message ?? ''}
                          onChange={e => {
                            const val = { ...level, message: e.target.value };
                            setLevels(prev => prev.map(l => l === level ? val : l));
                          }} />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </List>
            )}
          </Paper>
        )}
      </Box>
    </GenericPage>
  );
};

export default QuizzesPage;
